import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { catsApi, feedingsApi, userApi } from '../utils/api';
import { handleApiError } from '../utils/helpers';
import FeederCatCard from '../components/FeederCatCard';

const FeederDashboard = () => {
  const [stats, setStats] = useState({
    totalCats: 0,
    pendingRequests: 0,
    completedRequests: 0,
    totalFeedings: 0
  });
  const [recentCats, setRecentCats] = useState([]);
  const [recentFeedings, setRecentFeedings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch feeder stats using the dedicated API call
        const feederStats = await userApi.getFeederStats();
        
        // Fetch feeder's cats
        const catsData = await catsApi.getFeederCats();
        setRecentCats(catsData.slice(0, 4)); // Show first 4 cats
        
        // Fetch recent feedings
        const feedingsData = await feedingsApi.getFeederFeedings(0, 5);
        console.log('Dashboard feedings response:', feedingsData);
        
        // Handle different response formats
        if (Array.isArray(feedingsData)) {
          // Direct array response
          setRecentFeedings(feedingsData.slice(0, 5));
          console.log('Set feedings from array:', feedingsData);
        } else if (feedingsData && Array.isArray(feedingsData.data)) {
          // Response with data property
          setRecentFeedings(feedingsData.data.slice(0, 5));
          console.log('Set feedings from data property:', feedingsData.data);
        } else {
          console.error('Unexpected feedings response format:', feedingsData);
          setRecentFeedings([]);
        }
        
        // Update stats from the dedicated stats endpoint
        setStats({
          totalCats: feederStats.totalCats || 0,
          pendingRequests: feederStats.pendingRequests || 0,
          completedRequests: feederStats.completedRequests || 0,
          totalFeedings: feederStats.totalFeedings || 0
        });
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError(handleApiError(error));
      } finally {
        setLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  return (
    <div className="feeder-dashboard">
      <div className="page-header">
        <h1 className="page-title">Feeder Dashboard</h1>
        <p className="page-description">
          Welcome to your feeder dashboard! Monitor your cats and feeding activities
        </p>
      </div>
      
      {/* Enhanced Stats section */}
      <div className="dashboard-stats-container">
        <h2 className="section-title">Your Feeding Statistics</h2>
        <div className="dashboard-stats">
          <div className="stat-card">
            <div className="stat-icon">🐱</div>
            <h3 className="stat-value">{stats.totalCats}</h3>
            <p className="stat-label">Total Cats</p>
            <Link to="/feeder/my-cats" className="stat-action">View Cats</Link>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">✅</div>
            <h3 className="stat-value">{stats.completedRequests}</h3>
            <p className="stat-label">Completed Requests</p>
            <Link to="/feeder/feedings" className="stat-action">View History</Link>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">🍽️</div>
            <h3 className="stat-value">{stats.totalFeedings}</h3>
            <p className="stat-label">Total Feedings</p>
            <Link to="/feeder/feedings" className="stat-action">View All</Link>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">🔔</div>
            <h3 className="stat-value">{stats.pendingRequests}</h3>
            <p className="stat-label">Pending Requests</p>
            {stats.pendingRequests > 0 && (
              <Link to="/feeder/my-cats?filter=requests" className="stat-action">View Requests</Link>
            )}
          </div>
        </div>
      </div>
      
      {/* Loading state */}
      {loading && (
        <div className="loading-container">
          <div className="loading-spinner"></div>
        </div>
      )}
      
      {/* Error state */}
      {error && !loading && (
        <div className="error-message">
          <p>{error}</p>
          <button className="button" onClick={() => window.location.reload()}>
            Refresh Page
          </button>
        </div>
      )}
      
      {!loading && !error && (
        <>
          {/* My Cats section */}
          <div className="section-divider">
            <h2>My Cats</h2>
            <div className="divider-line"></div>
            <Link to="/feeder/my-cats" className="view-all-button">
              View All <span>&rarr;</span>
            </Link>
          </div>
          
          {recentCats.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🐱</div>
              <h2 className="empty-state-title">No cats yet</h2>
              <p className="empty-state-description">
                You haven't added any cats yet. Add your first cat to start tracking feedings.
              </p>
              <Link to="/feeder/add-cat" className="button">
                Add a Cat
              </Link>
            </div>
          ) : (
            <>
              <div className="my-cats-grid">
                {recentCats.map(cat => (
                  <FeederCatCard key={cat.id} cat={cat} />
                ))}
              </div>
            </>
          )}
          
          {/* Recent Feedings section */}
          <div className="section-divider">
            <h2>Recent Feedings</h2>
            <div className="divider-line"></div>
            <Link to="/feeder/feedings" className="view-all-button">
              View All <span>&rarr;</span>
            </Link>
          </div>
          
          {recentFeedings.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">🍽️</div>
              <h2 className="empty-state-title">No feedings yet</h2>
              <p className="empty-state-description">
                You haven't recorded any feedings yet. Feed a cat to get started.
              </p>
              {recentCats.length > 0 && (
                <Link to={`/feeder/create-feeding/${recentCats[0].id}`} className="button">
                  Feed {recentCats[0].name}
                </Link>
              )}
            </div>
          ) : (
            <>
              {/* Debug output */}
              <div style={{ display: 'none' }}>
                {console.log('Rendering feedings:', recentFeedings)}
              </div>
            
              <div className="feeder-feedings-list">
                {recentFeedings.map(feeding => {
                  // Debug output for each feeding
                  console.log('Processing feeding:', feeding);
                  console.log('Cat from feeding:', feeding.cat);
                  console.log('Cat photos:', feeding.cat?.catPhotos);
                  
                  // Extract cat photo from the nested structure - check multiple potential sources
                  let catPhoto = null;
                  
                  // Option 1: cat.catPhotos array
                  if (feeding.cat && feeding.cat.catPhotos && feeding.cat.catPhotos.length > 0) {
                    catPhoto = feeding.cat.catPhotos[0].path;
                    console.log('Using cat.catPhotos[0].path:', catPhoto);
                  }
                  // Option 2: cat.mainPhoto property
                  else if (feeding.cat && feeding.cat.mainPhoto) {
                    catPhoto = feeding.cat.mainPhoto;
                    console.log('Using cat.mainPhoto:', catPhoto);
                  }
                  // Option 3: standalone catPhoto property
                  else if (feeding.catPhoto) {
                    catPhoto = feeding.catPhoto;
                    console.log('Using feeding.catPhoto:', catPhoto);
                  }
                  // Option 4: standalone mainPhoto property
                  else if (feeding.mainPhoto) {
                    catPhoto = feeding.mainPhoto;
                    console.log('Using feeding.mainPhoto:', catPhoto);
                  }
                  
                  console.log('Final cat photo used:', catPhoto || '/cat-placeholder.png');
                  
                  const catName = feeding.cat ? feeding.cat.name : (feeding.catName || 'Unknown Cat');
                  
                  return (
                    <div key={feeding.id} className="feeding-item">
                      <div className="feeding-header">
                        <h3 className="feeding-title">
                          <img 
                            src={catPhoto || '/cat-placeholder.png'} 
                            alt={catName} 
                            className="feeding-cat-image"
                            onError={(e) => {
                              console.log('Image failed to load:', catPhoto);
                              e.target.src = '/cat-placeholder.png';
                            }}
                          />
                          {catName}
                        </h3>
                        
                        <div className="feeding-meta">
                          <span className="feeding-date">
                            {new Date(feeding.date).toLocaleString()}
                          </span>
                          <Link to={`/feeder/feedings/${feeding.id}`}>
                            View Details
                          </Link>
                        </div>
                      </div>
                      
                      <div className="feeding-details">
                        <div className="feeding-info">
                          {feeding.notes && <p className="feeding-notes"><strong>Notes:</strong> {feeding.notes}</p>}
                        </div>
                      </div>
                      
                      {/* Completed requests count display */}
                      {feeding.completedRequestsCount !== undefined && (
                        <div className="completed-requests-badge">
                          <span className="completed-requests-count">{feeding.completedRequestsCount}</span>
                          <span className="completed-requests-label">
                            {feeding.completedRequestsCount === 1 ? 'request' : 'requests'} completed
                          </span>
                        </div>
                      )}
                      
                      {feeding.feedingPhotos && feeding.feedingPhotos.length > 0 && (
                        <div className="feeding-photos">
                          {feeding.feedingPhotos.slice(0, 4).map((photo, index) => (
                            <div key={index} className="feeding-photo">
                              <img src={photo.path} alt={`${catName} feeding ${index + 1}`} />
                            </div>
                          ))}
                          {feeding.feedingPhotos.length > 4 && (
                            <div className="feeding-photo more-photos">
                              <div className="more-photos-overlay">
                                +{feeding.feedingPhotos.length - 4} more
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
};

export default FeederDashboard; 